import { useState, useCallback, useRef, useEffect } from 'react';
import styles from './AnnotationCanvas.module.css';
import { CANONICAL_WIDTH, LINE_HEIGHT_PX } from '@/previs/screenplayPaginator.ts';

export interface StrokePath {
  d: string;
  stroke: string;
  strokeWidth: number;
  opacity?: number;
}

export interface AnnotationData {
  paths: StrokePath[];
  viewBox: string;
}

interface AnnotationCanvasProps {
  contentRect: { x: number; y: number; width: number; height: number };
  onPathsChange: (paths: StrokePath[]) => void;
  strokeColor?: string;
  onFinish?: () => void;
  /** Initial pen contact point (clientX/clientY) — used to start the first stroke
   *  when drawing mode was auto-activated by an Apple Pencil touch. */
  initialPoint?: { clientX: number; clientY: number } | null;
  /** Pre-populated paths (snapshot from previous annotation). */
  initialPaths?: StrokePath[];
  /** Current tool — defaults to 'pen'. */
  tool?: 'pen' | 'eraser';
  /** Stroke width — defaults to 3. */
  strokeWidth?: number;
  /** Stroke opacity — defaults to 1. */
  strokeOpacity?: number;
}

// ── Eraser helpers ─────────────────────────────────────────────────────

function parsePathPoints(d: string): Array<{ x: number; y: number }> {
  const points: Array<{ x: number; y: number }> = [];
  const regex = /[ML]([\d.]+),([\d.]+)/g;
  let match;
  while ((match = regex.exec(d)) !== null) {
    points.push({ x: parseFloat(match[1]), y: parseFloat(match[2]) });
  }
  return points;
}

function isNearPath(path: StrokePath, nx: number, ny: number, threshold: number): boolean {
  const points = parsePathPoints(path.d);
  return points.some(p => (p.x - nx) ** 2 + (p.y - ny) ** 2 <= threshold * threshold);
}

const DEFAULT_STROKE_COLOR = '#c43c2d';
const DEFAULT_STROKE_WIDTH = 3;

export function AnnotationCanvas({ contentRect, onPathsChange, strokeColor = DEFAULT_STROKE_COLOR, onFinish, initialPoint, initialPaths, tool = 'pen', strokeWidth = DEFAULT_STROKE_WIDTH, strokeOpacity = 1 }: AnnotationCanvasProps) {
  const [paths, setPaths] = useState<StrokePath[]>(initialPaths ?? []);
  const svgRef = useRef<SVGSVGElement>(null);
  const isDrawing = useRef(false);
  const isErasing = useRef(false);

  // ── Imperative active-stroke refs ──────────────────────────────────────
  // The in-progress stroke is NOT managed by React state. We accumulate the
  // SVG path `d` string in a ref and write directly to the DOM via
  // activePathElRef. This avoids 120Hz+ React re-renders on Apple Pencil
  // and eliminates drawing flicker/lag.
  const currentPathRef = useRef('');
  const activePathElRef = useRef<SVGPathElement>(null);

  // Notify parent whenever paths change
  const pathsRef = useRef(paths);
  useEffect(() => {
    pathsRef.current = paths;
    onPathsChange(paths);
  }, [paths, onPathsChange]);

  const normalize = useCallback((e: React.PointerEvent) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return { x: 0, y: 0 };
    return {
      x: Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height)),
    };
  }, []);

  const ERASER_THRESHOLD = 0.03;

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();

    if (tool === 'eraser') {
      isErasing.current = true;
      svgRef.current?.setPointerCapture(e.pointerId);
      const { x, y } = normalize(e);
      setPaths(old => old.filter(p => !isNearPath(p, x, y, ERASER_THRESHOLD)));
      return;
    }

    isDrawing.current = true;
    svgRef.current?.setPointerCapture(e.pointerId);
    const { x, y } = normalize(e);
    const d = `M${x.toFixed(4)},${y.toFixed(4)}`;
    currentPathRef.current = d;
    activePathElRef.current?.setAttribute('d', d);
  }, [normalize, tool]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    // Apple Pencil hover fires pointermove with pressure 0 — ignore to prevent ghost strokes
    if (e.pointerType === 'pen' && e.pressure === 0) return;

    if (tool === 'eraser') {
      if (!isErasing.current) return;
      e.preventDefault();
      const { x, y } = normalize(e);
      setPaths(old => old.filter(p => !isNearPath(p, x, y, ERASER_THRESHOLD)));
      return;
    }

    if (!isDrawing.current) return;
    e.preventDefault();
    const { x, y } = normalize(e);
    currentPathRef.current += ` L${x.toFixed(4)},${y.toFixed(4)}`;
    activePathElRef.current?.setAttribute('d', currentPathRef.current);
  }, [normalize, tool]);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    if (tool === 'eraser') {
      isErasing.current = false;
      svgRef.current?.releasePointerCapture(e.pointerId);
      return;
    }

    if (!isDrawing.current) return;
    isDrawing.current = false;
    svgRef.current?.releasePointerCapture(e.pointerId);

    const d = currentPathRef.current;
    currentPathRef.current = '';
    activePathElRef.current?.removeAttribute('d');

    if (d) {
      setPaths(old => [...old, { d, stroke: strokeColor, strokeWidth, opacity: strokeOpacity !== 1 ? strokeOpacity : undefined }]);
    }
  }, [tool, strokeColor, strokeWidth, strokeOpacity]);

  // Seed the first stroke from the pen-down point that activated drawing mode.
  // AnnotationCanvas wasn't mounted yet when that event fired, so we replay it here.
  // The pen is still on the screen, so subsequent pointermove/pointerup events will
  // fire on the SVG and continue/finish the stroke normally.
  const didSeedRef = useRef(false);
  useEffect(() => {
    if (didSeedRef.current || !initialPoint) return;
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const x = Math.max(0, Math.min(1, (initialPoint.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (initialPoint.clientY - rect.top) / rect.height));
    const d = `M${x.toFixed(4)},${y.toFixed(4)}`;
    currentPathRef.current = d;
    activePathElRef.current?.setAttribute('d', d);
    isDrawing.current = true;
    didSeedRef.current = true;
  }, [initialPoint]);

  // Keyboard: Cmd+Z to undo stroke
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        setPaths(old => old.slice(0, -1));
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, []);

  // Enter key to finish drawing (skip if user is typing in an input/textarea)
  useEffect(() => {
    if (!onFinish) return;
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        onFinish();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onFinish]);

  // Escape key to exit drawing mode
  useEffect(() => {
    if (!onFinish) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onFinish();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onFinish]);

  if (contentRect.width <= 0 || contentRect.height <= 0) return null;

  return (
    <div
      className={styles.wrapper}
      style={{
        position: 'absolute',
        left: contentRect.x,
        top: contentRect.y,
        width: contentRect.width,
        height: contentRect.height,
        zIndex: 15,
      }}
    >
      <svg
        ref={svgRef}
        className={styles.canvas}
        viewBox="0 0 1 1"
        preserveAspectRatio="none"
        style={{ touchAction: 'none', cursor: tool === 'eraser' ? 'cell' : 'crosshair' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {/* Invisible hit-target rect so pointer events work on empty areas */}
        <rect x="0" y="0" width="1" height="1" fill="none" pointerEvents="all" />
        {paths.map((p, i) => (
          <path
            key={i}
            d={p.d}
            stroke={p.stroke}
            strokeWidth={p.strokeWidth}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            opacity={p.opacity ?? 1}
          />
        ))}
        {/* Active stroke — d attribute is set imperatively via ref, not by React */}
        <path
          ref={activePathElRef}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
          opacity={strokeOpacity}
        />
      </svg>
    </div>
  );
}

// ── Read-only annotation overlay (single) ─────────────────────────────

interface AnnotationOverlayProps {
  svgJson: string;
  contentRect: { x: number; y: number; width: number; height: number };
}

export function AnnotationOverlay({ svgJson, contentRect }: AnnotationOverlayProps) {
  let data: AnnotationData;
  try {
    data = JSON.parse(svgJson) as AnnotationData;
  } catch {
    return null;
  }

  if (!data.paths || data.paths.length === 0) return null;
  if (contentRect.width <= 0 || contentRect.height <= 0) return null;

  // Highlighter strokes embed the draw-time scale factor in their width
  // (Math.round(LINE_HEIGHT_PX * drawScaleFactor)). Recompute to match
  // the current display scale so the highlight covers exactly one line.
  const displayScale = contentRect.width / CANONICAL_WIDTH;
  const highlighterWidth = Math.round(LINE_HEIGHT_PX * displayScale);

  return (
    <svg
      className={styles.readOnly}
      style={{
        left: contentRect.x,
        top: contentRect.y,
        width: contentRect.width,
        height: contentRect.height,
      }}
      viewBox={data.viewBox || '0 0 1 1'}
      preserveAspectRatio="none"
    >
      {data.paths.map((p, i) => {
        // Highlighter strokes have sub-1 opacity; recalculate their width
        // to match the current display scale instead of the draw-time scale.
        const isHighlighter = p.opacity != null && p.opacity < 1;
        return (
          <path
            key={i}
            d={p.d}
            stroke={p.stroke}
            strokeWidth={isHighlighter ? highlighterWidth : p.strokeWidth}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            opacity={p.opacity ?? 1}
          />
        );
      })}
    </svg>
  );
}

// ── Multi-user annotation overlay (merges all doodles) ────────────────

interface MultiAnnotationOverlayProps {
  svgJsonList: string[];
  contentRect: { x: number; y: number; width: number; height: number };
}

export function MultiAnnotationOverlay({ svgJsonList, contentRect }: MultiAnnotationOverlayProps) {
  if (svgJsonList.length === 0) return null;
  if (contentRect.width <= 0 || contentRect.height <= 0) return null;

  const allPaths: StrokePath[] = [];
  for (const json of svgJsonList) {
    try {
      const data = JSON.parse(json) as AnnotationData;
      if (data.paths) allPaths.push(...data.paths);
    } catch { /* skip malformed */ }
  }

  if (allPaths.length === 0) return null;

  const displayScale = contentRect.width / CANONICAL_WIDTH;
  const highlighterWidth = Math.round(LINE_HEIGHT_PX * displayScale);

  return (
    <svg
      className={styles.readOnly}
      style={{
        left: contentRect.x,
        top: contentRect.y,
        width: contentRect.width,
        height: contentRect.height,
      }}
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
    >
      {allPaths.map((p, i) => {
        const isHighlighter = p.opacity != null && p.opacity < 1;
        return (
          <path
            key={i}
            d={p.d}
            stroke={p.stroke}
            strokeWidth={isHighlighter ? highlighterWidth : p.strokeWidth}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            opacity={p.opacity ?? 1}
          />
        );
      })}
    </svg>
  );
}
